import axios from 'axios';
import { LABELS } from '@/i18n/labels';
import type { Lang } from '@/stores/langStore';

/**
 * Translates a thrown login error into a localized, user-facing message.
 *
 * Accepts an optional `lang` so callers can pass the current UI language
 * for bilingual output. Defaults to `'en'` so existing tests that do not
 * supply a lang continue to work unchanged.
 *
 * Rules enforced here (T9.2 / OP-165):
 *   - No raw HTTP status codes in the returned string ("HTTP 418" appears
 *     only when the status has truly no mapping and the agent needs to report
 *     it — but it is formatted via the label template, not a raw string).
 *   - No word "backend" in any user-visible message.
 *   - No raw `error.message` from client-side exceptions (would leak stack
 *     info) — replaced with a generic localized prompt to retry.
 *
 * Distinct failure modes:
 *   - Network failure (no HTTP response) — backend down / DNS / IPv6 mismatch
 *   - 401 account_locked — too many failed attempts; wait or ask an admin to unlock
 *   - 401 / 400 — wrong credentials, the agent can fix by retyping
 *   - 403 — the server refused sign-in for a reason we cannot name; contact an admin.
 *     (A temporary-password account's 403 password_reset_required never reaches this
 *     function: LoginPage routes it to the change-password screen.)
 *   - 5xx — server-side failure, transient, retry is reasonable
 */
export function loginErrorMessage(error: unknown, lang: Lang = 'en'): string {
  const L = (key: keyof typeof LABELS) => LABELS[key][lang];

  if (axios.isAxiosError(error)) {
    // Network-layer failure — no HTTP response was received.
    if (!error.response) {
      return L('loginErrNetwork');
    }
    const status = error.response.status;
    // OP-224 — a LOCKED account answers 401 { error: "account_locked", retryAfterMinutes }.
    // It must read as a lock (wait, or ask an admin to unlock), not as "incorrect password":
    // a user who keeps retyping a correct password only extends the lock. Mirrors admin-portal.
    const data = error.response.data as { error?: string; retryAfterMinutes?: number } | undefined;
    if (data?.error === 'account_locked') {
      const mins =
        typeof data.retryAfterMinutes === 'number' && data.retryAfterMinutes > 0
          ? data.retryAfterMinutes
          : null;
      if (mins) {
        return (mins === 1 ? L('loginErrLockedMinutesOne') : L('loginErrLockedMinutesMany')).replace(
          '{n}',
          String(mins)
        );
      }
      return L('loginErrLockedGeneric');
    }
    if (status === 401 || status === 400) {
      return L('loginErrCredentials');
    }
    // A 403 here is NOT "locked": a lock is the 401 above, and a temporary-password
    // account (403 password_reset_required) is routed to the change-password screen
    // by LoginPage before this runs. What is left is a refusal we cannot name (e.g. the
    // company is unavailable), so say only that sign-in is not possible and who to ask.
    // The old text claimed "locked or suspended" for all of them and sent a user whose
    // account was fine to an administrator with nothing to unlock.
    if (status === 403) {
      return L('loginErrForbidden');
    }
    if (status >= 500) {
      return L('loginErrServer');
    }
    // Unmapped status — include the code so the agent can report it to IT,
    // but do NOT expose the word "backend" or raw error internals.
    return L('loginErrUnknownStatus').replace('{status}', String(status));
  }

  // Non-Axios throw — almost always a client-side bug (JWT decode, JSON
  // parse, etc.). Do NOT surface error.message to the user (it may contain
  // stack traces or internal paths); show a generic retry prompt instead.
  return L('loginErrGeneric');
}
